import type { CarTireTreadDepthLatestResponseRecord } from '@navio/nsdk';
import { TireSeason } from '@navio/nsdk';
import type { TreadDepthThresholds } from 'noddi-util';
import {
  WheelSetStatusType,
  treadDepthThresholdsFromLatest,
  wheelSetMeasurementsFromTreadDepthLatest,
  wheelSetStatusFromTreadDepthLatest
} from 'noddi-web';
import { getCommonTranslations } from '../../../utils/commonTranslations';

export type CarTireSeason = typeof TireSeason.SUMMER | typeof TireSeason.WINTER;

type WheelTreadDepth = {
  label: string;
  mm: number;
};

type SeasonTreadDepth = {
  status: WheelSetStatusType;
  /** Empty when the set lacks a measurement for any of the four corners. */
  wheels: WheelTreadDepth[];
  lowestMm: number | undefined;
  /** Null where the car's country regulates neither minimum. */
  thresholds: TreadDepthThresholds | null;
  measuredAt: string | undefined;
};

export const getSeasonTreadDepth = (
  latest: CarTireTreadDepthLatestResponseRecord | undefined,
  season: CarTireSeason
): SeasonTreadDepth => {
  const translations = getCommonTranslations().wheelMeasurements;
  const measurements = wheelSetMeasurementsFromTreadDepthLatest(latest, season);
  const thresholds = treadDepthThresholdsFromLatest(latest, season);
  const status = wheelSetStatusFromTreadDepthLatest(latest, season);

  if (!measurements) {
    return { status, wheels: [], lowestMm: undefined, thresholds, measuredAt: undefined };
  }

  const wheels: WheelTreadDepth[] = [
    { label: translations.leftFront, mm: measurements.frontLeft.value.value },
    { label: translations.rightFront, mm: measurements.frontRight.value.value },
    { label: translations.leftRear, mm: measurements.rearLeft.value.value },
    { label: translations.rightRear, mm: measurements.rearRight.value.value }
  ];
  const measuredAt = [measurements.frontLeft, measurements.frontRight, measurements.rearLeft, measurements.rearRight]
    .map((measurement) => measurement.measuredAt)
    .sort()
    .at(-1);

  return {
    status,
    wheels,
    lowestMm: Math.min(...wheels.map(({ mm }) => mm)),
    thresholds,
    measuredAt
  };
};

/** Opens on the season the customer is most likely heading into: winter from October through March. */
export const getCurrentSeason = (): CarTireSeason => {
  const month = new Date().getMonth();
  return month >= 9 || month <= 2 ? TireSeason.WINTER : TireSeason.SUMMER;
};
